import { useState } from "react"
import Link from "next/link"
import Image from "next/image"
import Script from "next/script"
import cn from "classnames"
import { getGlobals, getAllFAQQuestions } from "storyblok/api"
import Meta from "components/Meta"
import TitleBanner from "components/DynamicComponent/atoms/TitleBanner"
import Plus from "public/assets/plus.svg"
import getPlainText from "utilities/getPlainText"
import Dog from "public/assets/pets/golden-puppy.png"

export default function FAQArchive({ questions }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <Meta
        info={{
          title: "Frequently Asked Questions",
        }}
      />
      <TitleBanner
        blok={{
          heading: "Frequently Asked Questions",
          pattern: "dog",
        }}
      />
      <section className="px-8">
        <div className="max-w-screen-xl mx-auto pb-12 lg:py-12 lg:flex relative">
          <nav className="sticky top-0 lg:top-16 self-start lg:w-80 -mx-8 lg:mr-24 lg:ml-0 mb-12 shadow-md">
            <div className="bg-green-apple px-8 lg:px-4 flex justify-between">
              <h2 className=" text-black text-s3 py-3 lg:py-2">Jump To...</h2>
              <button className="lg:hidden w-5" onClick={() => setExpanded(!expanded)}>
                <Plus
                  className={cn("transition-all duration-300", {
                    "rotate-45": expanded,
                  })}
                />
              </button>
            </div>
            <div
              className={cn(
                "bg-faqNav bg-white bg-cover bg-right-bottom px-8 lg:px-4 py-4 transition-all duration-300 origin-top relative scale-y-100",
                {
                  "scale-y-0 h-0 opacity-0 py-0 lg:h-auto lg:scale-y-100 lg:opacity-100 lg:py-4": !expanded,
                }
              )}
            >
              {questions.map(([category]) => (
                <Link
                  href={`#${category}`}
                  className={cn(
                    "block capitalize underline hover:no-underline text-green-pine font-bold pb-4 max-w-max z-10 relative",
                    {
                      "hidden lg:block": !expanded,
                    }
                  )}
                  onClick={() => setExpanded(false)}
                  scroll={false}
                  key={category}
                >
                  {category}
                </Link>
              ))}
              <Image
                src={Dog}
                alt=""
                aria-hidden
                className={cn("absolute bottom-0 right-0 z-0 pointer-events-none", {
                  "hidden lg:block": !expanded,
                })}
              />
            </div>
          </nav>
          <div>
            {questions.map(([category, questions]) => (
              <div className="py-4 first:pt-0 last:pb-0 scroll-mt-24 lg:scroll-mt-4" id={category} key={category}>
                <h2 className="capitalize pb-4">{category}</h2>
                {questions.map((question) => (
                  <Link
                    href={`${question.full_slug}#faq-content`}
                    className="block max-w-max underline hover:no-underline text-green-pine font-bold pb-4"
                    key={question.full_slug}
                  >
                    {question.content.question}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Script type="application/ld+json" id="frequently-asked-questions-schema">
        {`{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            ${questions.map(([_, questionArray]) =>
              questionArray.map(
                (question) => `{
                    "@type": "Question",
                    "name": "${question.content.question}",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "${getPlainText(question.content.answer, { addNewlines: false })}"
                    }
                  }`
              )
            )}
          ]
        }`}
      </Script>
    </>
  )
}

export async function getStaticProps() {
  const globals = await getGlobals()
  const questions = await getAllFAQQuestions()

  return {
    props: {
      globals,
      questions,
    },
  }
}
